import { useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentIntentResult } from '@stripe/stripe-js';
import * as Linking from 'expo-linking';

import { PaymentMethodError } from '@errors';
import type {
  IPaymentMethodHandleCardAction,
  IPaymentMethodConfirmPayment,
  IPaymentMethod,
} from './usePaymentMethod';

export function usePaymentMethod(): IPaymentMethod {
  const stripe = useStripe();
  const elements = useElements();

  async function handleCardAction(
    params: IPaymentMethodHandleCardAction,
  ) {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    let result: null | PaymentIntentResult = null;

    if (params.requiredAction) {
      result = await stripe.handleCardAction(params.clientSecret);
    } else {
      result = await stripe.retrievePaymentIntent(
        params.clientSecret,
      );
    }

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return result.paymentIntent;
  }

  async function confirmSetup(params: IPaymentMethodConfirmPayment) {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await stripe.confirmSetup({
      clientSecret: params.clientSecret,
      redirect: 'if_required',
      elements,
      confirmParams: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return_url: Linking.createURL('payment/confirm', {
          queryParams: params.queryParams,
        }),
      },
    });

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return result.setupIntent;
  }

  async function confirmPayment(
    params: IPaymentMethodConfirmPayment,
  ) {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await stripe.confirmPayment({
      clientSecret: params.clientSecret,
      redirect: 'if_required',
      elements,

      confirmParams: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        return_url: Linking.createURL('payment/confirm', {
          queryParams: params.queryParams,
        }),
      },
    });

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return result.paymentIntent;
  }

  async function submit() {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await elements.submit();

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return;
  }

  async function retrievePaymentIntent(clientSecret: string) {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await stripe.retrievePaymentIntent(clientSecret);

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return result.paymentIntent;
  }

  async function retrieveSetupIntent(clientSecret: string) {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return null;
    }

    const result = await stripe.retrieveSetupIntent(clientSecret);

    if (result.error) {
      throw new PaymentMethodError(result.error);
    }

    return result.setupIntent;
  }

  const isLoaded = !!stripe && !!elements;

  return {
    handleCardAction,
    confirmPayment,
    confirmSetup,
    submit,
    retrievePaymentIntent,
    retrieveSetupIntent,
    isLoaded,
  };
}
